import { getMetaCookies } from './metaCookies';

const STORAGE_KEY = 'meta_attribution';

export type MetaAttribution = {
  fbp?: string;
  fbc?: string;
  fbclid?: string;
};

function isPaidMetaParams(params: URLSearchParams): boolean {
  return params.get('utm_source') === 'meta' && params.get('utm_medium') === 'paid';
}

/** Meta `_fbc` format when the cookie is missing but `fbclid` is in the URL. */
export function buildSyntheticFbc(fbclid: string, timestampMs = Date.now()): string {
  return `fb.1.${timestampMs}.${fbclid}`;
}

/**
 * Capture Meta click IDs on paid Meta landings (first-touch, overwrites stale storage).
 * Call alongside UTM capture on app load.
 */
export function captureMetaAttributionFromUrl(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const params = new URLSearchParams(window.location.search);
    if (!isPaidMetaParams(params)) {
      return;
    }

    const fbclid = params.get('fbclid') || undefined;
    const cookies = getMetaCookies();
    let fbc = cookies.fbc;
    if (!fbc && fbclid) {
      fbc = buildSyntheticFbc(fbclid);
    }

    const attr: MetaAttribution = {
      fbp: cookies.fbp,
      fbc,
      fbclid,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(attr));
  } catch {
    // ignore storage errors
  }
}

/** Merged cookie + stored attribution for CAPI relay and signup payloads. */
export function getMetaAttribution(): MetaAttribution {
  const cookies = getMetaCookies();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { ...cookies };
    }
    const stored = JSON.parse(raw) as MetaAttribution;
    // Treat fbp/fbc/fbclid as one unit per landing capture.
    const hasStoredClickIds = !!(stored.fbc || stored.fbclid);
    if (hasStoredClickIds) {
      return {
        fbp: stored.fbp || cookies.fbp,
        fbc: stored.fbc,
        fbclid: stored.fbclid,
      };
    }
    return {
      fbp: cookies.fbp || stored.fbp,
      fbc: cookies.fbc,
      fbclid: cookies.fbclid,
    };
  } catch {
    return { ...cookies };
  }
}

export function isPaidMetaLanding(): boolean {
  try {
    const params = new URLSearchParams(window.location.search);
    if (isPaidMetaParams(params)) {
      return true;
    }
    const raw = localStorage.getItem('utm_params');
    if (!raw) {
      return false;
    }
    const utm = JSON.parse(raw) as { utm_source?: string; utm_medium?: string };
    return utm.utm_source === 'meta' && utm.utm_medium === 'paid';
  } catch {
    return false;
  }
}
